/**
 * Player-Only Target Lock System
 * 
 * Rules:
 * - Only the player can lock onto enemies.
 * - Enemies NEVER have a lock on the player.
 * - Marker: small downward red triangle / diamond above selected enemy head.
 * - NO "LOCK" text. NO giant square box.
 * - Automatically auto-aims skills & attacks toward locked enemy.
 * - Clears automatically if enemy dies, moves out of range, or if toggled off.
 */

import { Enemy } from "../entities/Enemy";
import { PLAYER_CONFIG } from "../config/gameConfig";
import { anomaliAudio } from "../audio/AnomaliAudio";

export class TargetLockSystem {
  public lockedTarget: Enemy | null = null;

  /**
   * Toggle target lock on/off or cycle to the next closest enemy.
   */
  public toggleLock(playerX: number, playerY: number, enemies: Enemy[]) {
    if (this.lockedTarget) {
      // If already locked, check if there is another valid candidate to cycle to
      const validEnemies = enemies.filter(
        (e) => !e.isDead && Math.hypot(e.x - playerX, e.y - playerY) <= PLAYER_CONFIG.targetLock.maxDistance
      );

      if (validEnemies.length <= 1) {
        // Unlock
        this.clearLock();
        anomaliAudio.playSfx("ui_click");
        return;
      }

      // Switch to next enemy
      const currentIndex = validEnemies.indexOf(this.lockedTarget);
      const nextIndex = (currentIndex + 1) % validEnemies.length;
      this.lockedTarget = validEnemies[nextIndex];
      anomaliAudio.playSfx("target_lock");
      return;
    }

    // Find closest alive enemy within max lock distance
    let closestEnemy: Enemy | null = null;
    let closestDistSq = PLAYER_CONFIG.targetLock.maxDistance ** 2;

    for (const enemy of enemies) {
      if (enemy.isDead) continue;
      const distSq = (enemy.x - playerX) ** 2 + (enemy.y - playerY) ** 2;
      if (distSq < closestDistSq) {
        closestDistSq = distSq;
        closestEnemy = enemy;
      }
    }

    if (closestEnemy) {
      this.lockedTarget = closestEnemy;
      anomaliAudio.playSfx("target_lock");
    }
  }

  /**
   * Explicitly set target or unlock.
   */
  public clearLock() {
    this.lockedTarget = null;
  }

  /**
   * Validate current lock target state.
   */
  public update(playerX: number, playerY: number) {
    if (!this.lockedTarget) return;

    // Check if target is dead
    if (this.lockedTarget.isDead) {
      this.clearLock();
      return;
    }

    // Check distance limit
    const dist = Math.hypot(this.lockedTarget.x - playerX, this.lockedTarget.y - playerY);
    if (dist > PLAYER_CONFIG.targetLock.breakDistance) {
      this.clearLock();
    }
  }

  /**
   * Render the small downward diamond/triangle indicator above the locked enemy.
   * Matches the visual reference mockup: clean, small, no text, no big box.
   */
  public render(ctx: CanvasRenderingContext2D, now: number) {
    if (!this.lockedTarget || this.lockedTarget.isDead) return;

    const tx = this.lockedTarget.x;
    // Position arrow marker ~38px above target entity center with smooth floating bob
    const bobOffset = Math.sin(now * 0.009) * 3.5;
    const ty = this.lockedTarget.y - 38 + bobOffset;

    ctx.save();
    ctx.translate(tx, ty);

    // Neon crimson targeting glow
    ctx.shadowColor = "#ff2244";
    ctx.shadowBlur = 8;

    // 1. Primary Downward Chevron Arrow (Tanda Panah)
    ctx.fillStyle = "#ff2244";
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 1.5;

    ctx.beginPath();
    ctx.moveTo(0, 6);       // Sharp bottom arrow tip pointing down at enemy
    ctx.lineTo(-7, -7);     // Left wing
    ctx.lineTo(-3.5, -7);   // Left inner notch
    ctx.lineTo(0, -2);      // Center inner dip
    ctx.lineTo(3.5, -7);    // Right inner notch
    ctx.lineTo(7, -7);      // Right wing
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // 2. Secondary Mini Floating Accent Chevron
    ctx.strokeStyle = "#ff4466";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(-5, -11);
    ctx.lineTo(0, -6.5);
    ctx.lineTo(5, -11);
    ctx.stroke();

    // 3. Center White-Hot Core Ping
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.arc(0, 0, 1.5, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }
}
